import React from 'react';
import { Modal } from 'react-bootstrap';
import './TaskCancelledModal.scss';

interface TaskCancelledModalProps {
  show: boolean;
  /** Whether the worker currently has a live phone call in progress */
  liveCall?: boolean;
  /** Optional reason the requester gave for the cancellation */
  reason?: string | null;
  onAcknowledge: () => void;
}

/**
 * Blocking modal shown to the ops worker the moment the requester cancels
 * the task they are working on. Requires explicit acknowledgment — no
 * backdrop/escape dismissal — so a cancellation can't be missed in a busy
 * chat (especially phone-call tasks where the chat carries the transcript).
 */
const TaskCancelledModal: React.FC<TaskCancelledModalProps> = ({
  show,
  liveCall = false,
  reason,
  onAcknowledge,
}) => {
  return (
    <Modal
      show={show}
      onHide={onAcknowledge}
      centered
      backdrop='static'
      keyboard={false}
      className='task-cancelled-modal'
    >
      <Modal.Header>
        <Modal.Title>🚫 Task Cancelled</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {liveCall ? (
          <>
            <p className='cancelled-headline'>
              This task has been CANCELLED by the requester.
            </p>
            <p className='cancelled-instructions'>
              Wrap up the call <strong>quickly but politely</strong> — do{' '}
              <strong>NOT</strong> make any (further) commitments on the
              user&apos;s behalf, and do <strong>NOT</strong> just hang up.
            </p>
          </>
        ) : (
          <p className='cancelled-headline'>
            This task has been CANCELLED by the requester —{' '}
            <strong>stop work now</strong>.
          </p>
        )}
        {reason && (
          <div className='cancelled-reason'>
            <span className='cancelled-reason-label'>Reason:</span> {reason}
          </div>
        )}
        <p className='cancelled-note'>
          If you already did real work (a conversation, booking, or purchase),
          use &quot;Report Work Done&quot; on the task page so the results reach
          the requester.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button
          type='button'
          className='cancelled-acknowledge-btn'
          onClick={onAcknowledge}
        >
          Understood
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskCancelledModal;
